/**
 * Human-inspectable markdown rendering of a study — written as `study.md`
 * next to `study.json` in every per-study folder (local disk + Drive).
 *
 * The markdown is DERIVED data: `study.json` is the source of truth and
 * edits to the markdown are never read back. Regenerated on every save.
 *
 * Lives in @skjv/storage (like study-naming.ts) so the web app, the desktop
 * shell, and Node CLI tooling all render the exact same markdown.
 */
import { getBookInfo } from '@skjv/common';
export function studyMarkdown(study) {
    const title = (study.name || 'Untitled').trim();
    const reference = formatRange(study.startingLocation, study.endingLocation);
    const lines = [];
    lines.push('---');
    if (study.id)
        lines.push(`id: ${study.id}`);
    lines.push(`title: ${yamlString(title)}`);
    lines.push(`reference: ${yamlString(reference)}`);
    if (study.studyGroupId)
        lines.push(`studyGroupId: ${study.studyGroupId}`);
    if (study.createdAt)
        lines.push(`created: ${study.createdAt}`);
    if (study.updatedAt)
        lines.push(`updated: ${study.updatedAt}`);
    lines.push('format: skjv-study-markdown/1');
    lines.push('---');
    lines.push('');
    lines.push(`# ${title}`);
    lines.push('');
    lines.push(`**Passage:** ${reference}`);
    if (study.writingLocation) {
        lines.push(`**Writing verse:** ${formatRange(study.writingLocation)}`);
    }
    if (study.memoryStartingLocation) {
        lines.push(`**Memory verses:** ${formatRange(study.memoryStartingLocation, study.memoryEndingLocation)}`);
    }
    // Experimental — the reader's own notes on the passage; also seeds AI
    // commentary generation and prints on the PDF teacher pages.
    if (study.readerThoughts?.trim()) {
        lines.push('');
        lines.push('## Reader Thoughts');
        lines.push('');
        lines.push(blockquote(study.readerThoughts));
    }
    const scenes = study.scenes ?? [];
    if (scenes.length > 0) {
        lines.push('');
        lines.push('## Scenes');
        scenes.forEach((scene, i) => {
            lines.push('');
            lines.push(`### Scene ${i + 1}`);
            for (const description of scene.descriptions ?? []) {
                lines.push(`- ${description}`);
            }
        });
    }
    const assignments = study.assignments ?? [];
    if (assignments.length > 0) {
        lines.push('');
        lines.push('## Student Assignments');
        for (const assignment of assignments) {
            const name = assignment.student?.name || 'Unknown student';
            const range = assignment.startingLocation
                ? ` — ${formatRange(assignment.startingLocation, assignment.endingLocation)}`
                : '';
            lines.push('');
            lines.push(`### ${name}${range}`);
            const student = assignment.student;
            if (student?.readingLevel || student?.writingLevel) {
                lines.push(`- Levels: reading ${student.readingLevel ?? '—'}, writing ${student.writingLevel ?? '—'}`);
            }
            if (assignment.reviewQuestion) {
                lines.push(`- Review question: ${assignment.reviewQuestion}`);
            }
            if (assignment.readingFragment) {
                lines.push('');
                lines.push(blockquote(assignment.readingFragment));
            }
        }
    }
    const teacherAssignments = study.teacherAssignments ?? [];
    if (teacherAssignments.length > 0) {
        lines.push('');
        lines.push('## Teacher Assignments');
        for (const assignment of teacherAssignments) {
            const name = assignment.teacher?.name || 'Unknown teacher';
            const question = assignment.reviewQuestion ? `: ${assignment.reviewQuestion}` : '';
            lines.push(`- ${name}${question}`);
        }
    }
    const commentary = study.aiCommentary;
    if (commentary?.text) {
        lines.push('');
        const heading = commentary.pericopeTitle || 'AI Commentary';
        const ref = commentary.reference ? ` (${commentary.reference})` : '';
        lines.push(`## Commentary — ${heading}${ref}`);
        lines.push('');
        lines.push(commentary.text.trim());
    }
    lines.push('');
    lines.push('---');
    lines.push('');
    lines.push('_Generated from `study.json` — the JSON is the source of truth; edits here are not read back._');
    lines.push('');
    return lines.join('\n');
}
/**
 * Render markdown from a storage envelope (the on-disk JSON shape). Returns
 * null for tombstones and anything that does not look like a study record —
 * callers treat null as "no sidecar".
 */
export function studyMarkdownFromEnvelope(envelope) {
    try {
        const rec = envelope;
        if (!rec || rec.namespace !== 'studies' || rec.deletedAt)
            return null;
        const study = rec.value;
        if (!study || typeof study.name !== 'string' || !study.startingLocation)
            return null;
        return studyMarkdown(study);
    }
    catch {
        return null;
    }
}
/**
 * "Matthew 2:21", "Matthew 2:21-29", "Matthew 2:21-3:5",
 * "Matthew 2:21 - Luke 1:5" — the human-readable twin of studyFolderName.
 */
export function formatRange(start, end) {
    const startInfo = getBookInfo(start.testamentIndex, start.bookIndex);
    const startBook = startInfo?.name || 'Unknown';
    const startRef = `${startBook} ${start.chapterIndex + 1}:${start.verseIndex + 1}`;
    if (!end)
        return startRef;
    const sameBook = end.testamentIndex === start.testamentIndex && end.bookIndex === start.bookIndex;
    const sameChapter = sameBook && end.chapterIndex === start.chapterIndex;
    if (sameChapter) {
        return end.verseIndex > start.verseIndex ? `${startRef}-${end.verseIndex + 1}` : startRef;
    }
    if (sameBook)
        return `${startRef}-${end.chapterIndex + 1}:${end.verseIndex + 1}`;
    const endInfo = getBookInfo(end.testamentIndex, end.bookIndex);
    const endBook = endInfo?.name || 'Unknown';
    return `${startRef} - ${endBook} ${end.chapterIndex + 1}:${end.verseIndex + 1}`;
}
function yamlString(s) {
    return `"${s.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}
function blockquote(text) {
    return text
        .trim()
        .split('\n')
        .map((line) => `> ${line}`)
        .join('\n');
}
//# sourceMappingURL=study-markdown.js.map